<template>
	<Page>
		<BaseTypography tag="h1" weight="semi-bold" size="xxl">Tasks</BaseTypography>

		<div class="earn-info-list">
			<div class="infocard">
				<div class="icon-glow"><CheckCircleIcon /></div>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">Completed</BaseTypography>
					<BaseTypography weight="bold" size="l">{{ user?.tasksCompleted || 0 }}</BaseTypography>
				</div>
			</div>

			<div class="infocard">
				<div class="icon-glow"><CoinVerticalIcon /></div>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">You've earned</BaseTypography>
					<BaseTypography weight="bold" size="l">{{ user?.tasksEarn || 0 }}</BaseTypography>
				</div>
			</div>
		</div>

		<BaseTypography tag="h1" weight="semi-bold" size="xxl">Referrals</BaseTypography>

		<ReferralLink :link="referralLink" @click="copyReferralLink" />

		<div class="referral-info-list">
			<div class="infocard">
				<div class="icon-glow icon-stroke"><Layers01lIcon /></div>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">1st level</BaseTypography>
					<BaseTypography weight="bold" size="l">20%</BaseTypography>
				</div>
			</div>
			<div class="infocard infocard-space-around">
				<BaseTypography color="gray" weight="normal" size="l">{{ user?.referrals[0] || 0 }}</BaseTypography>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">You've earned</BaseTypography>
					<BaseTypography weight="bold" size="l">{{ user?.referralsEarn[0] || 0 }}</BaseTypography>
				</div>
			</div>

			<div class="infocard">
				<div class="icon-glow icon-stroke"><Layers02lIcon /></div>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">2nd level</BaseTypography>
					<BaseTypography weight="bold" size="l">10%</BaseTypography>
				</div>
			</div>
			<div class="infocard infocard-space-around">
				<BaseTypography color="gray" weight="normal" size="l">{{ user?.referrals[1] || 0 }}</BaseTypography>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">You've earned</BaseTypography>
					<BaseTypography weight="bold" size="l">{{ user?.referralsEarn[1] || 0 }}</BaseTypography>
				</div>
			</div>

			<div class="infocard">
				<div class="icon-glow icon-stroke"><Layers03lIcon /></div>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">3rd level</BaseTypography>
					<BaseTypography weight="bold" size="l">5%</BaseTypography>
				</div>
			</div>
			<div class="infocard infocard-space-around">
				<BaseTypography color="gray" weight="normal" size="l">{{ user?.referrals[2] || 0 }}</BaseTypography>

				<div class="infocard-content">
					<BaseTypography color="gray" weight="normal">You've earned</BaseTypography>
					<BaseTypography weight="bold" size="l">{{ user?.referralsEarn[2] || 0 }}</BaseTypography>
				</div>
			</div>

			<!-- <EarnInfoCard text="1st level" :value="user?.referrals[0] || 0" />
			<EarnInfoCard text="Earn" :value="user?.referralsEarn[0]?.toString() || '0'">
				<BaseTypography weight="bold" size="l">20%</BaseTypography>
			</EarnInfoCard>

			<EarnInfoCard text="2st level" :value="user?.referrals[1] || 0" />
			<EarnInfoCard text="Earn" :value="user?.referralsEarn[1]?.toString() || '0'">
				<BaseTypography weight="bold" size="l">10%</BaseTypography>
			</EarnInfoCard>

			<EarnInfoCard text="3st level" :value="10%" />
			<EarnInfoCard text="Earn" :value="user?.referralsEarn[2]?.toString() || '0'">
				<BaseTypography weight="bold" size="l">{{ user?.referrals[2] || 0 }}</BaseTypography>
			</EarnInfoCard> -->
		</div>
	</Page>
</template>

<script setup lang="ts">
import Page from '@/components/ui/Page.vue';
import { useTelegram } from '@/composables/useTelegram';
import ReferralLink from '@/features/referrals/components/ReferralLink.vue';
import { useToastStore } from '@/features/toasts/stores/useToastStore';
import MessageToast from '@/features/toasts/components/MessageToast.vue';
import EarnInfoCard from '@/features/earn/components/EarnInfoCard.vue';
import { useUser } from '@/features/tasks/hooks/useUser';
import CheckCircleIcon from '@/assets/icons/CheckCircle.svg?component';
import CoinVerticalIcon from '@/assets/icons/CoinVertical.svg?component';

import Layers01lIcon from '@/assets/icons/layers-01.svg?component';
import Layers02lIcon from '@/assets/icons/layers-02.svg?component';
import Layers03lIcon from '@/assets/icons/layers-03.svg?component';

import BaseTypography from '@/components/ui/BaseTypography.vue';

const { telegram } = useTelegram();
const toastStore = useToastStore();
// const commitHash = __COMMIT_HASH__;
// const version = __VERSION__;

const { data: user } = useUser();

const referralLink = `${import.meta.env.VITE_WEBAPP_URL}/?startapp=r_${telegram.WebApp.initDataUnsafe.user?.id}`;

const copyReferralLink = () => {
	navigator.clipboard.writeText(referralLink);

	toastStore.addToast({
		content: MessageToast,
		props: {
			message: 'Link copied'
		},
		status: 'success'
	});
};
</script>

<style scoped>
.line {
	cursor: pointer;
}

.line > *:first-child {
	display: flex;
	align-items: center;
	gap: var(--size-s);
}

.earn-info-list {
	display: grid;

	gap: 12px;

	grid-template-columns: 1fr 1fr;
}

.referral-info-list {
	display: grid;

	gap: 8px;

	grid-template-columns: 1fr 1fr;
}

.icon {
	stroke: rgba(75, 86, 117, 1);
	vertical-align: middle;
}

.icon-glow {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 10px;

	width: 40px;
	height: 40px;
	aspect-ratio: 1;

	background: radial-gradient(50% 50% at 50% 50%, rgba(2, 122, 255, 0.39) 0%, rgba(2, 122, 255, 0) 100%);
}

.icon-stroke > * {
	stroke: rgb(2, 122, 255) !important;
}

.infocard {
	box-sizing: border-box;

	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 12px;
	gap: 6px;

	background: linear-gradient(
		83.45deg,
		rgba(2, 122, 255, 0.05) -1.65%,
		rgba(248, 254, 255, 0.05) 52.48%,
		rgba(2, 122, 255, 0.05) 104.15%
	);
	border-radius: 16px;
}

.infocard-content {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0px;
	gap: 4px;
}

.infocard-space-around {
	justify-content: space-around;
}
</style>
